import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

export const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, ${props => props.$opacity || 0.92});
    backdrop-filter: blur(${props => props.$blur || '10px'});
    -webkit-backdrop-filter: blur(${props => props.$blur || '10px'});
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10001;
    padding: 1rem;
    animation: ${fadeIn} 0.3s ease;
    overflow: hidden;
`;

export const EffectsLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;
`;

export const ModalContainer = styled.div`
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: ${props => props.$borderRadius || '25px'};
    max-width: ${props => props.$maxWidth || '480px'};
    width: 100%;
    max-height: 90vh;
    overflow: hidden;
    position: relative;
    z-index: 1;
    box-shadow:
        0 25px 60px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    animation: ${slideUp} ${props => props.$animationDuration || '0.4s'} ease-out;

    @media (max-width: 520px) {
        max-width: calc(100% - 2rem);
        border-radius: 20px;
    }
`;

export const CloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.4);
    border: 2px solid rgba(255, 255, 255, 0.2);
    color: white;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 1.4rem;
    font-weight: 300;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10;
    line-height: 1;

    &:hover {
        background: rgba(255, 107, 139, 0.5);
        border-color: rgba(255, 107, 139, 0.7);
        transform: rotate(90deg) scale(1.1);
    }

    &:focus {
        outline: 2px solid #6b5bff;
        outline-offset: 2px;
    }
`;

export const PromoImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
    aspect-ratio: ${props => {
        const ratio = props.$aspectRatio || '4:5';
        return ratio.replace(':', '/');
    }};

    @media (max-width: 520px) {
        max-height: 300px;
        object-fit: cover;
    }
`;

export const ContentWrapper = styled.div`
    padding: 1.5rem 2rem 2rem;
    text-align: center;

    @media (max-width: 520px) {
        padding: 1.25rem 1.5rem 1.5rem;
    }
`;

export const Badge = styled.span`
    display: inline-block;
    background: ${props => props.$color || '#ff6b8b'};
    color: white;
    padding: 0.35rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 1rem;
`;

export const Title = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    background: linear-gradient(135deg, #ff6b8b, #6b5bff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.2;

    @media (max-width: 520px) {
        font-size: 1.6rem;
    }
`;

export const Subtitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin: 0 0 0.75rem 0;

    @media (max-width: 520px) {
        font-size: 1rem;
    }
`;

export const Description = styled.p`
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.6;
    margin: 0 0 1.5rem 0;

    @media (max-width: 520px) {
        font-size: 0.9rem;
    }
`;

export const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

export const PrimaryButton = styled.button`
    background: linear-gradient(135deg, #ff6b8b, #6b5bff);
    color: white;
    padding: 1rem 2rem;
    border: none;
    border-radius: 15px;
    font-size: 1.05rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(107, 91, 255, 0.4);
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 200%;
        height: 100%;
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
        );
        animation: ${shimmer} 3s infinite;
    }

    &:focus {
        outline: 2px solid white;
        outline-offset: 2px;
    }
`;

export const SecondaryButton = styled.button`
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    padding: 0.75rem 1.5rem;
    border: none;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        color: white;
    }

    &:focus {
        outline: 2px solid rgba(255, 255, 255, 0.5);
        outline-offset: 2px;
        border-radius: 8px;
    }
`;
